import { useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle, CardBody, Badge, Stepper } from '@vnetwork/ui/primitives';
import {
  useServiceActivationWorkflow,
  type ServiceDetail,
  type ServiceActivationTicket,
  type TicketWorkflowStep,
} from '@/api/services';
import { formatDateTime } from '@/lib/format';
import { WorkflowStepForm } from './workflow-step-form';
import {
  ACTIVE_TICKET_STATUSES,
  CSKH_STEP_NAMES,
  TECHNICAL_STEP_NAMES,
  STATUS_LABEL,
  stepLabel,
} from './workflow-step-labels';

const muted = { color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' } as const;

/** Pick the first still-active ticket, else the most recent one. */
function pickActiveTicket(tickets: ServiceActivationTicket[]): ServiceActivationTicket | undefined {
  return tickets.find((t) => ACTIVE_TICKET_STATUSES.includes(t.ticket_status)) ?? tickets[0];
}

/**
 * Internal-ticket activation workflow — the multi-step confirm flow (CS
 * verification → technical provisioning → completion) that sits at the top of a
 * service's Information tab. Renders the step progress; the active (`doing`)
 * step expands to its approve/reject form (CS steps) or an info note (technical
 * provisioning, handled in the provider console). Renders nothing when the
 * service has no internal ticket.
 */
export function ServiceActivationWorkflow({ detail }: { detail: ServiceDetail }) {
  const id = detail.service_id;
  const { data } = useServiceActivationWorkflow(id);
  const tickets = data ?? [];
  const ticket = useMemo(() => pickActiveTicket(tickets), [tickets]);

  const steps = useMemo(
    () => (ticket?.ticket_workflow_steps ?? []).slice().sort((a, b) => a.step_order - b.step_order),
    [ticket],
  );
  const doingStep = steps.find((s) => s.status === 'doing');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const activeId = expandedId ?? doingStep?.step_id ?? null;
  const expanded = steps.find((s) => s.step_id === activeId) ?? null;

  if (!ticket || steps.length === 0) return null;

  const isActive = ACTIVE_TICKET_STATUSES.includes(ticket.ticket_status);
  const doneCount = steps.filter((s) => s.status === 'done').length;

  // Active marker = the doing step; else the first not-yet-done step.
  const doingIndex = steps.findIndex((s) => s.status === 'doing');
  const current = doingIndex >= 0 ? doingIndex : Math.min(doneCount, steps.length - 1);
  // Navigable = every non-todo step (done / doing / blocked), matching the old
  // clickable rule. Stepper only fires onStepClick for steps at/before `reached`
  // that aren't the current one, so expose the furthest non-todo index.
  const lastNavigable = steps.reduce((max, s, i) => (s.status !== 'todo' ? i : max), -1);
  const reached = Math.max(current, lastNavigable);

  return (
    <Card flat>
      <CardHeader
        actions={<Badge tone={isActive ? 'primary' : 'neutral'} appearance="subtle" size="sm">{doneCount}/{steps.length} steps</Badge>}
      >
        <CardTitle>{ticket.ticket_type_title || 'Service activation'}</CardTitle>
      </CardHeader>
      <CardBody>
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Stepper
            aria-label="Service activation progress"
            steps={steps.map((s) => ({
              label: stepLabel(s.step_name),
              description: STATUS_LABEL[s.status],
            }))}
            current={current}
            reached={reached}
            error={doingStep?.status === 'blocked' || steps[current]?.status === 'blocked'}
            onStepClick={(i) => {
              const id = steps[i]?.step_id ?? null;
              setExpandedId((prev) => (prev === id ? null : id));
            }}
          />
          {expanded ? <StepDetail detail={detail} step={expanded} /> : null}
        </div>
      </CardBody>
    </Card>
  );
}

function StepDetail({ detail, step }: { detail: ServiceDetail; step: TicketWorkflowStep }) {
  const isDoing = step.status === 'doing';
  const isCskh = CSKH_STEP_NAMES.has(step.step_name);
  const isTechnical = TECHNICAL_STEP_NAMES.has(step.step_name);

  return (
    <Card flat style={{ background: 'var(--vn-bg-muted)' }}>
    <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
      <span style={{ fontWeight: 'var(--vn-font-weight-semibold)', fontSize: 'var(--vn-font-size-sm)' }}>{stepLabel(step.step_name)}</span>

      {isDoing && isCskh ? (
        <WorkflowStepForm detail={detail} step={step} />
      ) : isDoing && isTechnical ? (
        <span style={muted}>
          Technical provisioning is handled in the provider console — not available in the workspace yet.
        </span>
      ) : step.status === 'done' && step.approver_name ? (
        <div style={{ ...muted, display: 'grid', gap: 'var(--vn-space-1)' }}>
          <span>Approved by {step.approver_name}</span>
          {step.approved_at ? <span>on {formatDateTime(step.approved_at)}</span> : null}
        </div>
      ) : (
        <span style={muted}>{STATUS_LABEL[step.status]}.</span>
      )}
    </CardBody>
    </Card>
  );
}
